import { Playbook } from '@/types/playbook/playbook';
import { Bot, CheckCircle, Compass, Play, User, Zap } from 'lucide-react';

export const firstLoginWelcome: Playbook = {
  meta: {
    id: 'first-login-welcome',
    slug: 'first-login-welcome',
    categoryId: 'onboard-educate',
    name: 'The "First Login" Welcome Tour',
    tagline: 'Greet new users and show them the ropes.',
    description: 'When a user logs in for the first time, use a Genie to give them a personalized tour and guide them to their first success.',
    strategy: 'When a user logs in for the first time, use a Genie to give them a quick tour or point them to the most important first step.',
    benefits: [
      'Reduce time to first value',
      'Increase user activation rates',
      'Lower churn in first week',
      'Personalized onboarding experience'
    ],
    targetAudience: ['SaaS platforms', 'Software applications', 'Online services'],
    estimatedSetupTime: '20 minutes',
    difficulty: 'intermediate',
    tags: ['onboarding', 'first-time-users', 'activation', 'user-success'],
    isFeatured: true
  },
  seo: {
    metaTitle: 'First Login Welcome Playbook - Genie User Onboarding',
    metaDescription: 'Reduce churn with Genie-powered first login experiences. Guide new users to success with personalized welcome tours.',
    keywords: ['user onboarding', 'first login experience', 'user activation', 'welcome tour', 'Genie onboarding']
  },
  demoConfig: {
    triggerType: 'immediate',
    message: "🎉 Welcome aboard! I'm your personal guide. Let me show you around and help you get the most out of our platform. Would you like a quick tour, or do you have a specific goal in mind?",
    position: 'center'
  },
  demoScript: [
    { speaker: 'agent', message: "Welcome! I'm here to help you get started. Tour or specific goal?" },
    { speaker: 'user', message: "I'd like a quick tour please" },
    { speaker: 'agent', message: "Perfect! First, let me show you how to create your first project." },
    { speaker: 'user', message: "Sounds good!" },
    { speaker: 'agent', message: "Click the 'New Project' button in the top right. I'll guide you through each step!" }
  ],
  templateConfig: {
    firstMessage: "🎉 Welcome! I'm here to help you get started. Would you like a quick tour, or is there something specific you'd like to accomplish today?",
    systemPrompt: "You are an onboarding Genie. Help new users get oriented, understand key features, and achieve their first success quickly. Be encouraging and supportive.",
    tools: ['tutorial_links', 'feature_guides']
  },
  flowSteps: [
    {
      type: 'user',
      icon: User,
      genieIcon: 'user',
      label: 'New user first login',
      description: 'User completes registration and logs into platform for the first time'
    },
    {
      type: 'genie',
      icon: Bot,
      genieIcon: 'genie-lamp',
      label: 'Personalized greeting',
      description: 'Genie welcomes user by name and offers tailored onboarding based on signup context'
    },
    {
      type: 'action',
      icon: Compass,
      genieIcon: 'settings',
      label: 'Offers guided tour',
      description: 'Presents choice between quick tour or focused task completion'
    },
    {
      type: 'action',
      icon: Play,
      genieIcon: 'settings',
      label: 'Guides first success',
      description: 'Walks user through completing their first meaningful action in the platform'
    },
    {
      type: 'action',
      icon: Zap,
      genieIcon: 'settings',
      label: 'Highlights key features',
      description: 'Shows most valuable features relevant to user\'s use case or role'
    },
    {
      type: 'result',
      icon: CheckCircle,
      genieIcon: 'smile',
      label: 'Successful user activation',
      description: 'User understands platform value and completes first meaningful task'
    }
  ],
  metrics: {
    conversionRate: '78%',
    responseTime: 'Immediate',
    satisfactionScore: '4.7/5',
    costSavings: '$120k/year',
    timeToValue: '5 minutes'
  },
  testimonials: [
    {
      companyName: 'ProjectFlow Software',
      authorName: 'Mike Johnson',
      authorTitle: 'Product Manager',
      quote: 'First-week churn dropped 55% after implementing the welcome tour. Users find value much faster now.',
      metrics: '55% reduction in first-week churn'
    }
  ],
  resources: [
    {
      type: 'guide',
      title: 'User Onboarding Best Practices',
      description: 'Comprehensive guide to designing effective onboarding experiences',
      url: '/docs/onboarding-best-practices'
    },
    {
      type: 'template',
      title: 'Welcome Tour Scripts',
      description: 'Proven conversation flows for different user types and platforms',
      url: '/templates/welcome-tours'
    }
  ]
};